import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Label, Row, Table } from 'reactstrap';
import loader from 'src/assets/images/loader.gif';
import BetterBreadcrumb from 'src/components/Common/BetterBreadCrumb';
import DeleteInformationModal from 'src/components/DeleteInformation/DeleteInformationModal';
import { InputField } from 'src/components';
import { setFromErrors } from 'src/helpers';
import {
  useCreateDirectorAndSecretaryMutation,
  useDeleteDirectorAndSecretaryMutation,
  useGetDirectorAndSecretaryQuery,
} from 'src/rtk/features/setup/approval/approvalApi';
import * as Yup from 'yup';
import { config } from 'src/config';

const initialValues = {
  user_type: '',
  name: '',
  signature: null as File | null,
};

const DirectorAndSecretarySetup = () => {
  const { data, isLoading: isListLoading, isError: isListError } = useGetDirectorAndSecretaryQuery();
  const [createEntry, createState] = useCreateDirectorAndSecretaryMutation();
  const [deleteEntry, deleteState] = useDeleteDirectorAndSecretaryMutation();
  const [deleteId, setDeleteId] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const entries = data?.data || [];
  const hasUserType = (userType?: string) =>
    entries.some(
      (entry) =>
        entry.user_type?.trim().toLowerCase() === userType?.trim().toLowerCase(),
    );
  const hasDirector = hasUserType('Director');
  const hasSecretary = hasUserType('Secretary');
  const userTypeOptions = [
    { value: 'Director', label: 'Director' },
    { value: 'Secretary', label: 'Secretary' },
  ].filter((option) => !hasUserType(option.value));

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      user_type: Yup.string()
        .oneOf(['Director', 'Secretary'])
        .required('User Type is required')
        .test(
          'unique-user-type',
          'This user type has already been added',
          (value) => !value || !hasUserType(value),
        ),
      name: Yup.string().trim().required('Name is required'),
      signature: Yup.mixed().required('Signature is required'),
    }),
    onSubmit: (values) => {
      if (hasUserType(values.user_type)) {
        formik.setFieldError('user_type', 'This user type has already been added');
        return;
      }

      const formData = new FormData();
      formData.append('user_type', values.user_type);
      formData.append('name', values.name.trim());
      if (values.signature) formData.append('signature', values.signature);
      createEntry(formData);
    },
  });

  const error = createState.error as CustomError;

  useEffect(() => {
    if (createState.isSuccess) {
      toast.success('Director/Secretary created successfully');
      formik.resetForm();
    }
    if (createState.isError) {
      toast.error(error?.message || 'Something went wrong');
      if (error?.data) setFromErrors(error.data, formik.setErrors);
    }
  }, [createState.isSuccess, createState.isError]);

  useEffect(() => {
    if (deleteState.isSuccess) {
      toast.success('Director/Secretary deleted successfully');
      setShowDeleteModal(false);
      setDeleteId('');
    }
    if (deleteState.isError) toast.error('Error deleting Director/Secretary');
  }, [deleteState.isSuccess, deleteState.isError]);

  return (
    <div className="page-content">
      {(createState.isLoading || deleteState.isLoading) && (
        <div className="overlay"><img src={loader} alt="Loading" height={100} width={300} /></div>
      )}
      <Container fluid>
        <BetterBreadcrumb title="Director and Secretary Setup" />
        <Card className="mb-4">
          <CardHeader className="fw-bold fs-5">Create Director or Secretary</CardHeader>
          <CardBody>
            <Form onSubmit={(event) => { event.preventDefault(); formik.handleSubmit(); }}>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label for="user_type" className="required-field">User Type</Label>
                    <InputField type="select" options={userTypeOptions} {...formik.getFieldProps('user_type')} invalid={{ errors: formik.errors, touched: formik.touched }} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label for="name" className="required-field">Name</Label>
                    <InputField type="text" placeholder="Enter name" {...formik.getFieldProps('name')} invalid={{ errors: formik.errors, touched: formik.touched }} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label for="signature" className="required-field">Signature</Label>
                    <input id="signature" name="signature" type="file" accept="image/*" className={`form-control ${formik.touched.signature && formik.errors.signature ? 'is-invalid' : ''}`} onBlur={formik.handleBlur} onChange={(event) => formik.setFieldValue('signature', event.currentTarget.files?.[0] || null)} />
                    {formik.touched.signature && formik.errors.signature && <div className="invalid-feedback">{formik.errors.signature as string}</div>}
                  </FormGroup>
                </Col>
              </Row>
              <div className="text-center">
                <Button type="reset" color="danger" className="me-2" onClick={() => formik.resetForm()}>Reset</Button>
                <Button type="submit" color="success" disabled={createState.isLoading || (hasDirector && hasSecretary)}>Create</Button>
              </div>
              {hasDirector && hasSecretary && <div className="text-center text-muted mt-2">A Director and a Secretary have already been added.</div>}
            </Form>
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="fw-bold fs-5">Director and Secretary List</CardHeader>
          <CardBody>
            <div className="table-responsive text-nowrap text-center">
              <Table className="mb-0">
                <thead><tr><th>Sl.</th><th>User Type</th><th>Name</th><th>Signature</th><th>Action</th></tr></thead>
                <tbody>
                  {isListLoading ? <tr><td colSpan={5}>Loading...</td></tr> : isListError ? <tr><td colSpan={5} className="text-danger">Unable to load data.</td></tr> : entries.length ? entries.map((entry, index) => (
                    <tr key={entry.id || `${entry.user_type}-${index}`}>
                      <td>{index + 1}</td><td>{entry.user_type}</td><td>{entry.name}</td>
                      <td>{entry.signature ? <img src={/^https?:\/\//i.test(String(entry.signature)) ? String(entry.signature) : `${config.APP_URL || ''}${entry.signature}`} alt={`${entry.user_type} signature`} style={{ maxWidth: 120, maxHeight: 50 }} /> : 'N/A'}</td>
                      <td><Button color="danger" size="sm" disabled={deleteState.isLoading} onClick={() => { setDeleteId(entry.id || ''); setShowDeleteModal(true); }}>Delete</Button></td>
                    </tr>
                  )) : <tr><td colSpan={5} className="text-danger">No data available!</td></tr>}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Container>
      {showDeleteModal && <DeleteInformationModal isOpen={showDeleteModal} toggle={() => setShowDeleteModal(false)} id={deleteId} deleteInfomation={deleteEntry} />}
    </div>
  );
};

export default DirectorAndSecretarySetup;
